import React, { useEffect, useState } from 'react'
import { MdDeleteOutline } from 'react-icons/md'
import { v4 as uuidv4 } from 'uuid'

import { Button, Flex, Heading, Input, Text } from '@chakra-ui/react'

import { useReason } from '@/features/reasons/useReason'

import './quotationReasonCard.scss'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice.js'
import { useDispatch } from 'react-redux'
import { Card } from '@/components/Elements'
import { AddButton, DeleteButton } from '@/components/Elements/Button'
import { mandatoryMainCloseReasons } from '@/features/reasons/closeReaon.constant'
import ReasonItem from '@/features/reasons/components/ReasonItem'

const CloseReasonCard = ({ type, shouldShowSubReasons = true }) => {
  //State
  const [reasonsData, setReasonsData] = useState([])

  const [selectedMainReasonId, setSelectedMainReasonId] = useState('')
  const [newReasonName, setNewReasonName] = useState('')
  const [newSubReason, setNewSubReason] = useState('')
  const [isAddingMainReason, setIsAddingMainReason] = useState(false)
  const [isAddingSubReason, setIsAddingSubReason] = useState(false)

  const [error, setError] = useState('')

  //Hooks
  const dispatch = useDispatch()
  const { useFetchReasonsByType, useUpdateReason } = useReason()
  const updateReason = useUpdateReason()
  const { data: rawReasonsData } = useFetchReasonsByType(type)

  //Translated

  const selectedMainReason = reasonsData?.find((reason) => reason._id === selectedMainReasonId)

  useEffect(() => {
    setReasonsData(rawReasonsData?.results)
  }, [rawReasonsData])

  const handleAddReason = () => {
    if (newReasonName.trim() === '') {
      return
    }

    //Check if the reason is duplicated

    if (reasonsData.find((reason) => reason.name === newReasonName.trim())) {
      setError('Main Reason already exists !')
      return
    }

    const newId = uuidv4()
    const newReason = { _id: newId, subOptions: [], type: type, name: newReasonName.trim() }
    setNewReasonName('')
    setError('')
    setIsAddingMainReason(false)
    setReasonsData((prevReasons) => {
      return [...prevReasons, newReason]
    })
    if (selectedMainReasonId === '') {
      setSelectedMainReasonId(newId)
    }
  }

  const handleAddSubReason = () => {
    if (newSubReason.trim() === '') {
      return
    }

    setReasonsData((prevReasons) =>
      prevReasons.map((reason) => {
        return reason._id === selectedMainReasonId
          ? {
              ...reason,
              subOptions: [...reason.subOptions, newSubReason],
            }
          : reason
      })
    )

    setNewSubReason('')
    setIsAddingSubReason(false)
  }

  const handleDeleteMainReason = (reasonId) => {
    setReasonsData((prevReasons) => prevReasons.filter((reason) => reason._id !== reasonId))

    // If the deleted reason was selected, select the first available reason
    if (selectedMainReasonId === reasonId) {
      setSelectedMainReasonId('')
    }
  }

  const handleDeleteSubReason = (subReasonToDelete) => {
    setReasonsData((prevReasons) =>
      prevReasons.map((reason) => {
        return reason._id === selectedMainReasonId
          ? {
              ...reason,
              subOptions: reason.subOptions.filter((subReason) => subReason !== subReasonToDelete),
            }
          : reason
      })
    )
  }

  const handleSave = async () => {
    try {
      dispatch(showLoadingOverlay())

      const response = await updateReason.mutateAsync({ type, data: reasonsData })
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Card title={type === 'offer' ? 'Offer Close Reason' : 'Maintenance Renewal Lost Reason'}>
      <Flex gap="2">
        <Flex direction={'column'} width={'30%'} gap="2">
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Heading size={'md'}>Main Reason</Heading>
            <AddButton onClick={() => setIsAddingMainReason(true)} />
          </Flex>
          {isAddingMainReason && (
            <Flex direction={'column'} gap="2">
              <Flex gap="2">
                <Input
                  type="text"
                  value={newReasonName}
                  onChange={(e) => setNewReasonName(e.target.value)}
                  placeholder="Enter new reason"
                />
                <Button colorScheme="green" onClick={handleAddReason}>
                  Add
                </Button>
              </Flex>
              {error && (
                <Text color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}
            </Flex>
          )}
          {reasonsData?.map((reason) => (
            <ReasonItem
              key={reason._id}
              onClick={() => setSelectedMainReasonId(reason._id)}
              isSelected={selectedMainReasonId === reason._id}
              canDelete={!mandatoryMainCloseReasons.includes(reason.name)}
              reasonName={reason?.name ?? '(-)'}
              handleDelete={() => {
                handleDeleteMainReason(reason._id)
              }}
            />
          ))}
        </Flex>

        {shouldShowSubReasons && (
          <Flex direction={'column'} width={'30%'} gap="2">
            <Flex justifyContent={'space-between'} alignItems={'center'}>
              <Heading size={'md'}>Detailed Reason: {selectedMainReason?.name ?? '(-)'}</Heading>
              <AddButton onClick={() => setIsAddingSubReason(true)} />
            </Flex>

            {selectedMainReason?.name !== '' && isAddingSubReason && (
              <Flex direction={'column'} gap="2">
                <Flex gap="2">
                  <Input
                    type="text"
                    value={newSubReason}
                    onChange={(e) => setNewSubReason(e.target.value)}
                    placeholder="Enter sub-reason"
                  />
                  <Button colorScheme="green" onClick={handleAddSubReason}>
                    Add
                  </Button>
                </Flex>
              </Flex>
            )}
            {Array.isArray(selectedMainReason?.subOptions) &&
              selectedMainReason?.subOptions.map((subReason, index) => (
                <ReasonItem
                  key={index}
                  canDelete={true}
                  reasonName={subReason}
                  handleDelete={() => {
                    handleDeleteSubReason(subReason)
                  }}
                />
              ))}
          </Flex>
        )}
      </Flex>
      <Button mt="6" colorScheme="blue" onClick={handleSave}>
        Save
      </Button>
    </Card>
  )
}

export default CloseReasonCard
